import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ConvertStringToReact } from '../../../../components/ConvertStringToReact';
import { AlertBanner } from '../../../../components/AlertBanner';
import { CommonButton } from '../../../../components/CommonButton';
import { Checkbox } from '../../../../components/Checkbox';
import { Spinner } from '../../../../components/Spinner';
import { gtagEnvelope } from '../../../../util/gtagEnvelope';
import styles from '../../Trial.module.css';
import { Formik, Form, Field, FormikProps } from 'formik';
import { SendAsyncPostObject, TrialViewProps, ZoomInfoResponse } from '~/common/types';
import { validateEmail } from '~/common/util/customValidation';
import { businessEmailCheck } from '~/common/util/businessEmail';
import { FormikEffect } from '~/common/components/FormikEffect';
import { handleStandardTrialSubmission } from '~/common/util/submissions/trialSubmission';
import { getErrorMessage } from '~/common/util/getErrorMessage';
import { emailCheck } from '~/common/util/emailCheck';
import { GreenPerson, Sheild, UserIcon, Password, Nav, Vault, Reporting, Business } from '~/common/components/SvgIcons';
import { postSegmentService, postTrialInterestBoomiService } from '~/common/services/trackingServices';
import { PRODUCT_SKUS, TRIAL_TYPES } from '~/common/consts';
import { handlePremiumTrialSubmission } from '~/common/util/submissions/premiumSubmission';
import { checkUserValidity, getZoomInfoData } from '~/common/services/sitecoreServices';
import { BusinessInputTest } from '../../BusinessInputTest';
import { premiumData } from './premiumData';
import { getEventName, getEventReason } from '~/common/util/getExperimentVersion';
import { useAppStore } from '~/common/store/appStore';
import { useShallow } from 'zustand/react/shallow';
import { getWebEnrichedFields } from '~/common/util/getWebEnrichedFields';
import { useLatestAbortSignal } from '~/common/hooks/useLatestAbortSignal';

type ApiResp = {
  success: boolean;
  errorMessage: string;
  code: number;
};

export const TrialViewUnified = (props: TrialViewProps) => {
  const {
    initialValues,
    formError,
    formErrorCode,
    data,
    trialAllowed,
    trackError,
    setErrorMessage,
    setErrorCode,
    existingUserEmail,
    isCanadaUser,
  } = props;

  const { setTrialLength, setTrialType, trialLength, ctaClassNameModifier } = useAppStore(
    useShallow((state) => ({
      setTrialLength: state.setTrialLength,
      setTrialType: state.setTrialType,
      trialLength: state.trialLength,
      ctaClassNameModifier: state.ctaClassNameModifier,
    }))
  );

  const isMobile = window.innerWidth < 900;

  const [showHint, setShowHint] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [contactPermission, setContactPermission] = useState(true);
  const [isBannerClosed, setIsBannerClosed] = useState(false);
  const [marketingOptOutSeen, setMarketingOptOutSeen] = useState(false);
  const [displayUnsubscribeCheckbox, setDisplayUnsubscribeCheckbox] = useState(false);
  const [existingAccount, setExistingAccount] = useState(false);
  const [step, setStep] = useState(1);
  const [premiumSelected, setPremiumSelected] = useState(true);
  const [businessError, setBusinessError] = useState<ApiResp>();
  const [premiumError, setPremiumError] = useState<ApiResp>();
  const [mistypedEmail, setMistypedEmail] = useState<string>('');
  const [zoomInfoData, setZoomInfoData] = useState<ZoomInfoResponse | null>(null);
  const formikRef = useRef<FormikProps<SendAsyncPostObject>>(null);
  const nextEmailTypeCheckSignal = useLatestAbortSignal();
  const nextStepCheckSignal = useLatestAbortSignal();
  const nextSubmitSignal = useLatestAbortSignal();

  // data overwrites
  useEffect(() => {
    data.inputs.email.label = 'Email address *';
    data.inputs.email.error = 'Please enter a valid email address';
    data.errors = [
      {
        code: '1000',
        codeText: 'INVALID_SKU',
        message: 'Sku is not valid',
      },
      {
        code: '1001',
        codeText: 'INVALID_FORM_FIELD_VALUE',
        message: 'Please complete all required fields marked with an asterisk (*).',
      },
      {
        code: '2000',
        codeText: 'INCOMPATIBLE_ACCOUNT',
        message:
          'You can’t use that address to start a LastPass Business trial. Go back and try again with a different email. \u003Ca href="https://link.lastpass.com/cannot-start-business-trial"\u003ELearn more\u003C/a\u003E\n\u003Cbr /\u003E\nError code: 2000',
      },
      {
        code: '2001',
        codeText: 'COMPANY_IS_MANAGED',
        message:
          'You can’t use that address to start a LastPass Business trial. Go back and try again with a different email. \u003Ca href="https://link.lastpass.com/cannot-start-business-trial"\u003ELearn more\u003C/a\u003E\n\u003Cbr /\u003E\nError code: 2001',
      },
      {
        code: '2002',
        codeText: 'BOSS_MIGRATION_RQUIRED',
        message:
          'We couldn’t start your trial. Go back and try again. \u003Ca href="https://link.lastpass.com/cannot-start-business-trial"\u003ELearn more\u003C/a\u003E\n\u003Cbr /\u003E\nError code: 2002',
      },
      {
        code: '2003',
        codeText: 'TRIAL_NOT_AVAILABLE',
        message:
          'You can’t use that address to start a LastPass Business trial. Go back and try again with a different email. \u003Ca href="https://link.lastpass.com/cannot-start-business-trial"\u003ELearn more\u003C/a\u003E\n\u003Cbr /\u003E\nError code: 2003',
      },
      {
        code: '2004',
        codeText: 'NOT_AN_ADMIN_IN_GROUP_ACCOUNT',
        message:
          'You can’t use that address to start a LastPass Business trial. Go back and try again with a different email. \u003Ca href="https://link.lastpass.com/cannot-start-business-trial"\u003ELearn more\u003C/a\u003E\n\u003Cbr /\u003E\nError code: 2004',
      },
      {
        code: '2005',
        codeText: 'USER_DEACTIVATED',
        message:
          'We couldn’t start your trial. Go back and try again. \u003Ca href="https://link.lastpass.com/cannot-start-business-trial"\u003ELearn more\u003C/a\u003E\n\u003Cbr /\u003E\nError code: 2005',
      },
      {
        code: '3001',
        codeText: 'FORBIDDEN_DOMAIN',
        message:
          'You can’t use that address to start a LastPass Business trial. Go back and try again with a different email. \u003Ca href="https://link.lastpass.com/cannot-start-business-trial"\u003ELearn more\u003C/a\u003E\n\u003Cbr /\u003E\nError code: 3001',
      },
      {
        code: '3002',
        codeText: 'INVALID_DOMAIN',
        message:
          'We couldn’t validate that address. Go back and try again with a valid email. \u003Ca href="https://link.lastpass.com/cannot-start-business-trial"\u003ELearn more\u003C/a\u003E\n\u003Cbr /\u003E\nError code: 3002',
      },
      {
        code: '3003',
        codeText: 'EMAIL_INVALID_FOR_REGISTRATION',
        message:
          'We couldn’t validate that address. Go back and try again with a valid email. \u003Ca href="https://link.lastpass.com/cannot-start-business-trial"\u003ELearn more\u003C/a\u003E\n\u003Cbr /\u003E\nError code: 3003',
      },
      {
        code: '2006',
        codeText: 'USER_AUTHENTICATION_REQUIRED',
        message:
          '\u003Ca href="https://lastpass.com/saml/launch/sitecore?RelayState=/trial/business"\u003ELog in to your account \u003C/a\u003Eto start a LastPass Business trial. \u003Ca href="https://link.lastpass.com/cannot-start-business-trial"\u003ELearn more\u003C/a\u003E\r\n\u003Cbr /\u003E\r\nError code: 2006',
      },
      {
        code: '2007',
        codeText: 'Incomplete_registration',
        message:
          'We couldn’t start your trial. Go back and try again. \u003Ca href="https://link.lastpass.com/cannot-start-business-trial"\u003ELearn more\u003C/a\u003E\n\u003Cbr /\u003E\nError code: 2007',
      },
      {
        code: '3004',
        codeText: 'Compliance_Checks_Registration',
        message:
          'Access or use of LastPass services in your region violates applicable export control and sanction laws and is prohibited.\n\u003Cbr /\u003E\nError code: 3004',
      },
    ];
  }, []);

  useEffect(() => {
    if (existingAccount) {
      formikRef.current?.setFieldError('email', 'error');
    }
  }, [existingAccount]);

  const setClosedBanner = (value: boolean) => {
    if (value !== isBannerClosed) {
      setIsBannerClosed(value);
    }
  };

  const trackErrorBlur = useCallback(
    (id: string, validation: (value: string | number) => string | boolean, value: string) => {
      const trimmed = value ? value.trim() : value;
      formikRef.current?.setFieldValue(id, trimmed);
      formikRef.current?.setFieldError(id, undefined);
      const isInvalid = validation(trimmed);
      setTimeout(() => {
        formikRef.current?.setFieldTouched(id, true);
        if (isInvalid) {
          formikRef.current?.setFieldError(id, 'error');
          gtagEnvelope('event', 'trial_page_error', {
            error_type: 'form_field_error',
            error_field: id,
            validation_failure_reason: isInvalid,
          });
        }
      }, 300);
      if (isInvalid) {
        return false;
      }
    },
    []
  );

  const validateBusinessEmail = useCallback(async (email: string) => {
    const signal = nextEmailTypeCheckSignal();
    setExistingAccount(false);
    formikRef.current?.setFieldTouched('email', true);
    setShowHint(false);
    const invalid = validateEmail(email);
    if (invalid) {
      formikRef.current?.setFieldError('email', 'error');
      return false;
    }
    const business = await businessEmailCheck(email.toLowerCase(), signal);
    if (signal.aborted) return false;
    if (!business) {
      setShowHint(true);
    }
    return false;
  }, []);

  const setOptOutOfEmails = () => {
    gtagEnvelope('event', 'cta_click', {
      cta_name: 'Email opt-out',
      cta_position: 'Trial Form',
    });
    setContactPermission(!contactPermission);
  };

  const unsubscribeClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'SPAN') {
      setMarketingOptOutSeen(true);
      gtagEnvelope('event', 'cta_click', {
        cta_name: 'Unsubscribe',
        cta_position: 'Trial Form',
      });
      setDisplayUnsubscribeCheckbox(!displayUnsubscribeCheckbox);
    }
  };

  useEffect(() => {
    setPremiumSelected(showHint);
    updateGlobalVals(showHint);
  }, [showHint]);

  const updateGlobalVals = (isPremium: boolean) => {
    if (isPremium) {
      setTrialLength(30);
      setTrialType(TRIAL_TYPES.PREMIUM);
    } else {
      setTrialLength(14);
      setTrialType(TRIAL_TYPES.BUSINESS);
    }
  };

  useEffect(() => {
    setClosedBanner(false);
    setErrorMessage(null);
  }, [premiumSelected]);

  const handleSub = async (
    values: SendAsyncPostObject,
    zoomEnrichedFields?: string,
    zoomContactToken?: string | null
  ) => {
    const signal = nextSubmitSignal();
    updateGlobalVals(premiumSelected);

    if (premiumSelected) {
      await postSegmentService({
        event: `${getEventName(true)}_trial_form_clicked`,
        properties: {
          journey_step: 'Step 2 select trial',
          sku: 'LPPremium',
        },
      });
      if (!premiumError.success) {
        if (businessError.code === 2006) {
          setExistingAccount(true);
        }
        setErrorMessage(getErrorMessage(premiumData, premiumError.code.toString()) || data.globalError);
        trackError({
          code: premiumError.code.toString(),
          message:
            premiumError.errorMessage ||
            getErrorMessage(premiumData, businessError.code.toString()) ||
            data.globalError,
          step,
        });
        return;
      }
    }
    try {
      setFormSubmitting(true);
      setIsBannerClosed(false);
      setErrorMessage(null);
      setErrorCode(null);

      let submitResponse;

      if (premiumSelected) {
        submitResponse = await handlePremiumTrialSubmission({
          data: premiumData,
          values,
          marketingOptOutSeen,
          contactPermission,
          signal,
        });
      } else {
        await postSegmentService({
          event: `enterprise_trial_form_click`,
          properties: {
            reason: 'business_trial_start',
            journey_step: `Step 2`,
          },
        });
        const webEnrichedFields = zoomEnrichedFields ?? (zoomInfoData ? getWebEnrichedFields(zoomInfoData) : undefined);
        const contactToken = zoomContactToken ?? zoomInfoData?.contactToken ?? undefined;
        submitResponse = await handleStandardTrialSubmission({
          data,
          existingUserEmail,
          values,
          marketingOptOutSeen,
          contactPermission,
          webEnrichedFields,
          contactToken,
          signal,
        });
      }
      if (signal.aborted) return;

      if (submitResponse && !submitResponse.success) {
        if (submitResponse.code === 2006) {
          setExistingAccount(true);
        }
        setErrorMessage(getErrorMessage(data, submitResponse.code, existingUserEmail) || data.globalError);
        trackError({
          code: submitResponse.code,
          message: submitResponse.errorMessage || getErrorMessage(data, submitResponse.code) || data.globalError,
          step,
        });
        setFormSubmitting(false);
      }
    } catch (error) {
      if (signal.aborted) return;
      setErrorMessage(data.globalError);
      trackError({
        code: '500',
        message: `Trial submission: ${error.message}`,
        runtime: true,
        step,
      });
      setFormSubmitting(false);
    }
  };

  const goToNextStep = async () => {
    gtagEnvelope('event', 'cta_click', {
      cta_name: 'Next step',
      cta_position: 'Trial Form',
    });
    const signal = nextStepCheckSignal();
    setFormSubmitting(true);
    setErrorMessage(null);
    setIsBannerClosed(false);
    try {
      if (!formikRef.current?.values?.email || validateEmail(formikRef.current?.values?.email.toString())) {
        return;
      }
      await postSegmentService({
        event: `${getEventName(true)}_trial_form_clicked`,
        properties: {
          journey_step: 'Step 1 email',
          sku: 'UNIFIED',
          marketingOptOutShown: marketingOptOutSeen,
          contactPermission: contactPermission,
        },
      });
      const formEmail = formikRef.current.values.email.toString();
      const businessBossInvalid = await emailCheck(formEmail, signal);
      if (signal.aborted) return;
      setBusinessError(businessBossInvalid);
      setTrialType(TRIAL_TYPES.PREMIUM);
      const premiumBossInvalid = await checkUserValidity(
        {
          email: formEmail,
          trialLength: 30,
          trialSku: 'LPPremiumTrial',
        },
        signal
      );
      if (signal.aborted) return;
      if (premiumBossInvalid && premiumBossInvalid.code === 1002 && premiumBossInvalid.correctedEmail) {
        setMistypedEmail(`${premiumData.inputs?.email?.placeHolder} ${premiumBossInvalid.correctedEmail}`);
        return;
      }
      setPremiumError(premiumBossInvalid);
      setExistingAccount(false);
      setStep(2);
      updateGlobalVals(showHint);
      postTrialInterestBoomiService({
        email: formEmail,
        contactPermission: contactPermission,
        trialSku: 'Modal',
      });
      await postSegmentService({
        event: `${getEventName(true)}_trial_form_shown`,
        properties: {
          journey_step: 'Step 2 select trial',
          sku: premiumSelected ? PRODUCT_SKUS.SKU_LPPREMIUM : PRODUCT_SKUS.SKU_LPBUSINESS,
        },
      });
    } catch (error) {
      if (signal.aborted) return;
      setErrorMessage(data.globalError);
      trackError({
        code: '500',
        message: `Trial submission: ${error.message}`,
        runtime: true,
      });
    } finally {
      if (!signal.aborted) setFormSubmitting(false);
    }
  };

  const resetStep3State = () => {
    if (zoomInfoData) {
      setZoomInfoData(null);
    }
    formikRef.current?.setValues(
      {
        ...formikRef.current.values,
        fullName: '',
        title: '',
        company: '',
        employees: '',
        temp_phone__c: '',
      },
      false
    );
  };

  const goToStep3 = async () => {
    gtagEnvelope('event', 'cta_click', {
      cta_name: 'Next Step - Business Trial form',
      cta_position: 'Trial Modal - Business',
    });
    postSegmentService({
      event: `${getEventName(true)}_trial_form_clicked`,
      properties: {
        journey_step: 'Step 2 select trial',
        sku: PRODUCT_SKUS.SKU_LPBUSINESS,
      },
    });
    updateGlobalVals(premiumSelected);
    if (!businessError.success) {
      if (businessError.code === 2006) {
        setExistingAccount(true);
      }
      setErrorMessage(getErrorMessage(data, businessError.code.toString()) || data.globalError);
      trackError({
        code: businessError.code.toString(),
        message: businessError.errorMessage || getErrorMessage(data, businessError.code.toString()) || data.globalError,
        step,
      });
      return;
    }
    const formEmail = formikRef.current?.values?.email?.toString();
    const { trialType: latestTrialType, businessMax } = useAppStore.getState();
    const isZoomInfoWorkflow =
      window.LPFeatureFlags?.enableZoomInfoEnrichApi &&
      (latestTrialType === TRIAL_TYPES.BUSINESS || businessMax) &&
      formEmail;
    if (isZoomInfoWorkflow) {
      setFormSubmitting(true);
      const res = await getZoomInfoData(formEmail);
      setFormSubmitting(false);
      if (res?.isComplete) {
        const zoomInfoValues: SendAsyncPostObject = {
          ...formikRef.current?.values,
          fullName: res.hasFirstName && res.hasLastName ? null : formikRef.current?.values.fullName,
          title: res.hasJobTitle ? null : formikRef.current?.values.title,
          company: res.hasCompanyName ? null : formikRef.current?.values.company,
          employees: res.hasCompanyEmployeeCount ? null : formikRef.current?.values.employees,
        };
        await handleSub(zoomInfoValues, getWebEnrichedFields(res), res.contactToken);
        return;
      }
      if (res && res.success !== false) {
        setZoomInfoData(res);
      }
    }
    setStep(3);
    postSegmentService({
      event: `enterprise_trial_form_shown`,
      properties: {
        reason: 'business_trial_start',
        journey_step: 'Step 2',
      },
    });
  };

  useEffect(() => {
    if (existingAccount || mistypedEmail) {
      formikRef.current?.setFieldError('email', 'error');
    }
  }, [existingAccount, mistypedEmail]);

  return (
    <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row' }}>
      <div
        style={{
          flex: 1,
          padding: isMobile ? '24px' : '48px',
          maxWidth: '512px',
        }}
      >
        <svg
          width="104"
          height="16"
          viewBox="0 0 104 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ marginBottom: '24px', marginLeft: '-10px' }}
        >
          <path
            d="M27.7891 3.77832C29.3364 3.77838 30.4713 4.12085 31.1934 4.80469C31.9148 5.48804 32.2763 6.48484 32.2764 7.79297V14.4844C32.2763 14.8406 32.135 15.086 31.8525 15.2197C31.5247 15.3681 31.1706 15.4948 30.791 15.5986C30.4116 15.7031 30.0207 15.7839 29.6191 15.8438C29.2168 15.903 28.8181 15.9442 28.4238 15.9668C28.029 15.9887 27.6532 16 27.2959 16C26.3133 16 25.4981 15.8992 24.8506 15.6982C24.2035 15.4974 23.69 15.23 23.3105 14.8955C22.9311 14.5616 22.6661 14.1675 22.5176 13.7139C22.3684 13.2607 22.2939 12.7948 22.2939 12.2744C22.294 11.7542 22.3795 11.2525 22.5508 10.7695C22.7214 10.2865 23.0002 9.86266 23.3877 9.49805C23.7746 9.13406 24.2775 8.8401 24.8955 8.61719C25.5128 8.39427 26.2687 8.28223 27.1621 8.28223H29.6631V7.90332C29.6631 7.62118 29.6324 7.35963 29.5732 7.12207C29.5135 6.88411 29.398 6.67623 29.2275 6.49805C29.0563 6.31974 28.8219 6.18211 28.5244 6.08496C28.2268 5.98847 27.8396 5.94043 27.3633 5.94043C26.8568 5.94044 26.306 5.98409 25.7109 6.07324C25.1151 6.16241 24.6309 6.26663 24.2588 6.38574C24.0953 6.43016 23.9762 6.43797 23.9023 6.4082C23.8278 6.37827 23.7747 6.29675 23.7451 6.16309L23.4326 4.8916C23.3728 4.65408 23.4404 4.49739 23.6338 4.42285C24.2592 4.1853 24.9442 4.01805 25.6885 3.9209C26.4326 3.82446 27.1323 3.77638 27.7871 3.77637L27.7891 3.77832ZM67.5332 3.77832C69.0815 3.77832 70.2163 4.11946 70.9385 4.80273C71.6605 5.48605 72.0214 6.48073 72.0215 7.78809V14.4736C72.0214 14.8301 71.8794 15.0753 71.5967 15.209C71.269 15.3573 70.9155 15.4832 70.5361 15.5869C70.1567 15.6907 69.7659 15.7728 69.3643 15.832C68.9626 15.8912 68.5642 15.9321 68.1699 15.9541C67.7749 15.9761 67.3994 15.9883 67.042 15.9883C66.0592 15.9883 65.2442 15.8878 64.5967 15.6875C63.9499 15.4866 63.4362 15.2187 63.0566 14.8848C62.6771 14.5507 62.4128 14.1563 62.2637 13.7031C62.1153 13.2502 62.0411 12.786 62.041 12.2666C62.041 11.7471 62.1257 11.2456 62.2969 10.7627C62.4675 10.2796 62.7478 9.85617 63.1348 9.49219C63.5217 9.12826 64.0237 8.83422 64.6416 8.61133C65.2596 8.38842 66.0148 8.27735 66.9082 8.27734H69.4092V7.89941C69.4092 7.61734 69.3801 7.35665 69.3203 7.11914C69.2612 6.88092 69.1449 6.67345 68.9736 6.49512C68.8023 6.31681 68.5681 6.17851 68.2705 6.08203C67.9729 5.98558 67.5856 5.9375 67.1094 5.9375C66.6037 5.93758 65.9317 6.07039 65.5596 6.18945C65.3959 6.23332 65.2767 6.24173 65.2021 6.21191C65.1276 6.18266 65.0755 6.10037 65.0459 5.9668L64.7334 4.69629C64.6736 4.45888 64.7404 4.3031 64.9336 4.22852C65.5582 3.99102 66.8781 3.77647 67.5332 3.77637V3.77832ZM58.2578 0.165039C59.0429 0.165074 59.7609 0.286476 60.4131 0.529297C61.0646 0.772816 61.6225 1.11735 62.0859 1.56445C62.5494 2.01158 62.9106 2.5456 63.1699 3.16504L63.1689 3.16699C63.4282 3.78643 63.5586 4.46496 63.5586 5.20215C63.5586 5.62529 63.4914 6.13162 63.3584 6.71973C63.2247 7.30792 62.957 7.87685 62.5566 8.42578C62.1556 8.97474 61.5942 9.44223 60.8721 9.82617C60.1494 10.2107 59.1995 10.4033 58.0215 10.4033H55.3027V15.5781C55.3027 15.7969 55.1232 15.9745 54.9023 15.9746H52.9961C52.7751 15.9746 52.5958 15.797 52.5957 15.5781V13.5781H52.5967V1.50586C52.5967 1.48935 52.5977 1.47192 52.5977 1.4541V1.03906C52.5977 0.556061 52.9935 0.165118 53.4805 0.165039H58.2578ZM46.9326 1.32715C47.0514 1.29731 47.1482 1.3085 47.2227 1.36035C47.2964 1.41293 47.334 1.52009 47.334 1.68359V4.5166H49.5781C49.7414 4.5166 49.842 4.54998 49.8789 4.61719C49.9158 4.6837 49.9346 4.7693 49.9346 4.87305V6.08789C49.9346 6.22153 49.9158 6.31869 49.8789 6.37793C49.842 6.43715 49.7414 6.46777 49.5781 6.46777H47.334V12.4639C47.334 12.9401 47.446 13.267 47.6689 13.4453C47.892 13.6236 48.2348 13.7129 48.6963 13.7129C48.7856 13.7129 48.9046 13.7085 49.0537 13.7012C49.202 13.6939 49.3544 13.6826 49.5107 13.668C49.6673 13.6533 49.8239 13.6349 49.9805 13.6123C50.1368 13.5897 50.2671 13.5719 50.3711 13.5566C50.5194 13.5274 50.6161 13.5302 50.6611 13.5674C50.7061 13.6053 50.7362 13.6765 50.751 13.7803L50.9961 15.207V15.2061C51.0109 15.2951 51.0152 15.3733 51.0078 15.4404C50.9998 15.5076 50.9295 15.5717 50.7959 15.6309C50.2901 15.7946 49.7832 15.891 49.2773 15.9209C48.7709 15.9502 48.3765 15.9648 48.0938 15.9648C46.8727 15.9648 46.0098 15.7048 45.5039 15.1846C44.9974 14.6642 44.7441 13.8685 44.7441 12.7979L44.8027 6.46777H43.1592C43.008 6.46777 42.9297 6.42885 42.8672 6.3623C42.8048 6.29582 42.7729 6.23044 42.8018 6.12207C42.8309 6.01309 43.3689 4.66021 43.3984 4.61719C43.4427 4.55801 43.5082 4.51667 43.6846 4.5166H44.7842V2.35742C44.7843 2.06303 44.9202 2.00939 44.9873 1.9502C45.0538 1.89106 45.1694 1.83226 45.333 1.77246L46.9326 1.32715ZM14.502 0C14.5476 2.66294e-06 14.5921 0.00817835 14.6338 0.0214844C14.7827 0.0687986 14.8968 0.193424 14.9277 0.347656C14.9331 0.374939 14.9365 0.403027 14.9365 0.431641V11.6416C14.9366 12.2662 15.0933 12.7077 15.4062 12.9639C15.7192 13.22 16.1483 13.3486 16.6943 13.3486H19.5635C19.8462 13.348 20.1284 13.3477 20.4111 13.3477C20.5763 13.347 20.7484 13.3467 20.9082 13.3467C21.0681 13.3467 21.2291 13.3412 21.3701 13.4316C21.3915 13.4449 21.4106 13.4613 21.4287 13.4785C21.5516 13.597 21.5561 13.8243 21.585 13.9854C21.6205 14.1828 21.6565 14.3806 21.6914 14.5781C21.7384 14.8435 21.7847 15.1087 21.8311 15.374H21.832C21.9169 15.9751 20.9314 15.9473 20.5586 15.9473H16.2383C15.4033 15.9473 14.7124 15.8271 14.167 15.5869C13.621 15.3467 13.1882 15.0252 12.8672 14.624C12.5462 14.2235 12.3207 13.7675 12.1924 13.2539C12.0634 12.7409 12 12.2032 12 11.6416V0.431641C12 0.391715 12.0055 0.352028 12.0156 0.31543C12.0506 0.190462 12.1416 0.0883072 12.2598 0.0371094C12.3135 0.0138424 12.3728 0 12.4346 0H14.502ZM37.8887 3.92383C38.5134 3.91318 39.1402 3.95718 39.7676 4.05566C40.3948 4.15481 41.0007 4.31555 41.585 4.53906C41.7045 4.59562 41.7773 4.66771 41.8008 4.75488C41.8249 4.84203 41.8225 4.94445 41.7949 5.06152L41.3037 6.31934C41.2614 6.43705 41.2061 6.50423 41.1396 6.51953C41.0725 6.53481 40.9789 6.52237 40.8594 6.48047C40.2757 6.28619 39.7632 6.15572 39.3232 6.08984C38.8834 6.024 38.4099 5.99626 37.9043 6.00488C37.3535 6.01486 36.9533 6.13531 36.7041 6.36621C36.4552 6.59694 36.3333 6.8507 36.3379 7.12793C36.3419 7.33288 36.3861 7.50795 36.4707 7.65234C36.5547 7.79659 36.6876 7.92978 36.8682 8.05078C37.0488 8.17185 37.286 8.29548 37.5781 8.42188C37.871 8.5483 38.2273 8.68191 38.6465 8.82031C39.1253 8.98729 39.5938 9.16557 40.0518 9.35449C40.5091 9.54411 40.9149 9.77447 41.2695 10.0459L41.2676 10.0479C41.6223 10.32 41.9108 10.6436 42.1338 11.0195C42.356 11.3954 42.472 11.8535 42.4814 12.3936C42.4915 12.9778 42.3892 13.4918 42.1729 13.9336C41.9566 14.3754 41.6536 14.7427 41.2627 15.0342C40.8725 15.3256 40.4096 15.5456 39.875 15.6934C39.3396 15.8411 38.7586 15.921 38.1318 15.9316C37.7739 15.9376 37.3928 15.9258 36.9893 15.8965C36.5857 15.8672 36.1816 15.8198 35.7773 15.7539C35.3739 15.6881 34.984 15.5952 34.6094 15.4775C34.2339 15.3604 33.888 15.2156 33.5723 15.0459C33.3474 14.9328 33.2924 14.7586 33.4072 14.5225L33.9219 13.2861C33.9937 13.1538 34.0668 13.0682 34.1406 13.0303C34.2145 12.9931 34.3338 13.0093 34.499 13.0791C35.039 13.3179 35.6374 13.5012 36.2949 13.6289C36.9518 13.756 37.5336 13.8153 38.04 13.8066C38.6648 13.796 39.1321 13.6671 39.4404 13.4209C39.7486 13.1741 39.8992 12.8612 39.8926 12.4814C39.8852 12.0583 39.6788 11.7249 39.2725 11.4834C38.8668 11.2425 38.2588 10.9822 37.4502 10.7041C36.8357 10.4959 36.2992 10.2861 35.8418 10.0752C35.3843 9.86426 35.0035 9.62586 34.7012 9.36035C34.3983 9.09558 34.1664 8.79615 34.0039 8.46289C33.8421 8.13026 33.7574 7.74405 33.75 7.30566C33.7413 6.82406 33.8236 6.38005 33.9961 5.97559C34.1694 5.57113 34.4295 5.21962 34.7773 4.9209C35.1253 4.62212 35.5598 4.38403 36.0811 4.20703C36.6015 4.03014 37.2038 3.93584 37.8887 3.92383ZM77.6328 3.92383C78.2575 3.91319 78.8841 3.95721 79.5107 4.05566C80.1382 4.15481 80.7447 4.31548 81.3291 4.53906C81.4485 4.5956 81.5204 4.66778 81.5439 4.75488C81.5681 4.84204 81.5666 4.94442 81.5391 5.06152L81.0469 6.31934C81.0046 6.43674 80.9501 6.50407 80.8838 6.51953C80.8167 6.53482 80.7229 6.52231 80.6035 6.48047C80.0199 6.28622 79.5073 6.15574 79.0674 6.08984C78.6274 6.02397 78.1533 5.99623 77.6475 6.00488C77.0967 6.01487 76.6975 6.13534 76.4482 6.36621C76.1991 6.597 76.0775 6.85061 76.082 7.12793C76.0861 7.33288 76.1292 7.50795 76.2139 7.65234C76.2978 7.79663 76.4308 7.92975 76.6113 8.05078C76.7919 8.17183 77.0293 8.2955 77.3213 8.42188C77.6142 8.5483 77.9705 8.68191 78.3896 8.82031C78.8686 8.98732 79.3368 9.16552 79.7949 9.35449C80.2524 9.54413 80.658 9.77442 81.0127 10.0459L81.0117 10.0479C81.3663 10.3193 81.6556 10.6436 81.8779 11.0195C82.1001 11.3953 82.2171 11.8536 82.2266 12.3936C82.2366 12.9778 82.1326 13.4918 81.917 13.9336C81.7007 14.3754 81.3978 14.7427 81.0068 15.0342C80.6159 15.3256 80.1538 15.5456 79.6191 15.6934C79.0845 15.8411 78.5036 15.921 77.877 15.9316C77.519 15.9376 77.1379 15.9257 76.7344 15.8965C76.3308 15.8672 75.9267 15.8197 75.5225 15.7539C75.1188 15.688 74.7283 15.5953 74.3535 15.4775C73.978 15.3604 73.6321 15.2156 73.3164 15.0459C73.0917 14.9328 73.0366 14.7586 73.1514 14.5225L73.665 13.2861C73.7376 13.1537 73.8109 13.0682 73.8848 13.0303C73.9587 12.9933 74.0774 13.0095 74.2422 13.0791C74.7823 13.318 75.3814 13.5011 76.0391 13.6289C76.6959 13.756 77.2778 13.8153 77.7842 13.8066C78.4086 13.796 78.8753 13.6669 79.1836 13.4209C79.4918 13.1741 79.6433 12.8612 79.6367 12.4814C79.6293 12.0582 79.422 11.7249 79.0156 11.4834C78.6099 11.2426 78.002 10.9829 77.1934 10.7041C76.5788 10.4958 76.0431 10.2868 75.585 10.0752C75.1276 9.86429 74.7476 9.6258 74.4453 9.36035C74.1432 9.09563 73.9109 8.79609 73.749 8.46289C73.5865 8.13026 73.5006 7.74405 73.4932 7.30566C73.4844 6.82399 73.567 6.3801 73.7402 5.97559C73.9135 5.57102 74.1735 5.21967 74.5215 4.9209C74.8694 4.62215 75.303 4.38403 75.8242 4.20703C76.3448 4.03003 76.9476 3.93581 77.6328 3.92383ZM87.4062 3.92383C88.0309 3.91319 88.6568 3.95721 89.2842 4.05566C89.9109 4.15481 90.5171 4.31548 91.1016 4.53906C91.2211 4.59562 91.2939 4.66771 91.3174 4.75488C91.3415 4.84202 91.3391 4.94447 91.3115 5.06152L90.8203 6.31934C90.778 6.43704 90.7227 6.5042 90.6562 6.51953C90.5891 6.53484 90.4955 6.52238 90.376 6.48047C89.7922 6.28617 89.2798 6.15572 88.8398 6.08984C88.3998 6.02397 87.9258 5.99623 87.4199 6.00488C86.8693 6.01491 86.4692 6.13536 86.2207 6.36621C85.9717 6.59696 85.8499 6.85066 85.8545 7.12793C85.8585 7.33288 85.9017 7.50795 85.9863 7.65234C86.0703 7.79673 86.2041 7.92968 86.3848 8.05078C86.5661 8.17183 86.8027 8.29551 87.0947 8.42188C87.3876 8.54828 87.7433 8.68193 88.1631 8.82031C88.6418 8.98728 89.1104 9.16559 89.5684 9.35449C90.025 9.5441 90.4316 9.7745 90.7861 10.0459C91.1405 10.3172 91.4291 10.641 91.6514 11.0166V11.0195C91.8736 11.3954 91.9906 11.8534 92 12.3936C92.0101 12.9778 91.9067 13.4918 91.6904 13.9336C91.4735 14.3754 91.1705 14.7427 90.7803 15.0342C90.39 15.3256 89.9272 15.5456 89.3926 15.6934C88.858 15.8411 88.277 15.921 87.6504 15.9316C87.2925 15.9376 86.9114 15.9257 86.5078 15.8965C86.1048 15.8672 85.6996 15.8198 85.2959 15.7539C84.891 15.688 84.5016 15.5953 84.127 15.4775C83.7514 15.3604 83.4056 15.2156 83.0898 15.0459C82.8651 14.9328 82.81 14.7586 82.9248 14.5225L83.4385 13.2861C83.511 13.1537 83.5843 13.0682 83.6582 13.0303C83.7321 12.9933 83.8508 13.0095 84.0156 13.0791C84.5557 13.318 85.1549 13.5011 85.8125 13.6289C86.4693 13.756 87.0519 13.8153 87.5576 13.8066C88.182 13.796 88.6487 13.6669 88.957 13.4209C89.2652 13.1741 89.4167 12.8612 89.4102 12.4814C89.4028 12.0582 89.1955 11.7249 88.7891 11.4834C88.3833 11.2426 87.7754 10.9829 86.9668 10.7041C86.3522 10.4958 85.8158 10.2868 85.3584 10.0752C84.901 9.86429 84.521 9.6258 84.2188 9.36035C83.9159 9.0956 83.684 8.79614 83.5215 8.46289C83.3596 8.13026 83.274 7.74405 83.2666 7.30566C83.2579 6.82399 83.3411 6.3801 83.5137 5.97559C83.6863 5.57103 83.947 5.21967 84.2949 4.9209C84.6429 4.62215 85.0764 4.38403 85.5977 4.20703C86.1183 4.03003 86.7211 3.93581 87.4062 3.92383ZM27.4961 10.2676C27.243 10.2676 26.9717 10.2823 26.6816 10.3115C26.3914 10.3415 26.1154 10.4239 25.8555 10.5576C25.5949 10.6913 25.3786 10.884 25.208 11.1367C25.0368 11.3895 24.9512 11.7389 24.9512 12.1846C24.9512 12.824 25.1667 13.2706 25.5986 13.5234C26.0305 13.7763 26.6037 13.9023 27.3184 13.9023C27.4218 13.9023 27.5678 13.8979 27.7539 13.8906C27.9399 13.8833 28.1369 13.8649 28.3457 13.835C28.5546 13.805 28.7739 13.7682 29.0049 13.7236C29.2348 13.6791 29.4465 13.6193 29.6396 13.5449V10.2676H27.4961ZM67.2432 10.2607C66.9907 10.2608 66.7188 10.2757 66.4287 10.3057C66.1385 10.3349 65.8632 10.4171 65.6025 10.5508C65.342 10.6845 65.1263 10.8772 64.9551 11.1299C64.7838 11.3827 64.6982 11.7319 64.6982 12.1777C64.6983 12.8165 64.9145 13.2625 65.3457 13.5146C65.7769 13.7668 66.3499 13.8935 67.0645 13.8936C67.1686 13.8936 67.314 13.8892 67.5 13.8818C67.686 13.8745 67.8836 13.8561 68.0918 13.8262C68.3007 13.7969 68.5206 13.7594 68.751 13.7148C68.9813 13.6703 69.1933 13.6106 69.3867 13.5361V10.2607H67.2432ZM55.1533 7.97754H58.0146C58.9421 7.97749 59.6355 7.76431 60.0938 7.33691L60.1816 7.24805C60.637 6.76166 60.8643 6.14182 60.8643 5.38867C60.8642 5.12193 60.8213 4.83152 60.7354 4.51758C60.6487 4.2036 60.4987 3.91796 60.2871 3.65918C60.0755 3.40035 59.7818 3.18472 59.4043 3.01172C59.0274 2.83939 58.5478 2.75293 57.9668 2.75293H55.1533V7.97754Z"
            fill="#D71340"
          />
        </svg>
        <h2 className={styles.header} style={{ marginBottom: '12px' }}>
          {step === 1 ? 'Create an account' : step === 2 ? 'Let’s get you started...' : 'Set up your profile'}
        </h2>
        {(step === 1 || step === 2) && (
          <p style={{ marginBottom: '28px' }}>
            {step === 1
              ? 'Set yourself up for success in 2 minutes or less.'
              : 'What brings you here today? This gets you the right LastPass service. '}
          </p>
        )}
        {step === 2 && (
          <AlertBanner
            closedBanner={formError !== null || isBannerClosed}
            setClosedBanner={setClosedBanner}
            type={'error'}
          >
            <ConvertStringToReact htmlString={formError || data.globalError} errorCode={formErrorCode} />
          </AlertBanner>
        )}
        {existingUserEmail && (
          <div className={styles.user}>
            <div className={styles.userInfo}>
              <div className={styles.userIcon}>
                <UserIcon />
              </div>{' '}
              {existingUserEmail}
            </div>
            <a href={data.logoutLink}>{data.logoutText}</a>
          </div>
        )}
        {step === 2 && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '18px',
              marginBottom: '24px',
            }}
          >
            <button
              style={{
                borderRadius: '8px',
                border: '1px solid #D5DBE2',
                padding: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                background: premiumSelected ? '#EBF2FA' : '#fff',
              }}
              onClick={() => {
                setPremiumSelected(true);
                gtagEnvelope('event', 'modal_radio_click', {
                  cta_name: 'Just Me',
                  cta_position: 'Unified Trial Modal - Premium',
                });
                postSegmentService({
                  event: `${getEventName(true)}_trial_form_radio_clicked`,
                  properties: {
                    journey_step: 'Step 2 select trial',
                    sku: 'LPPremium',
                  },
                });
              }}
            >
              <div style={{ position: 'relative' }}>
                <div
                  style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '100%',
                    border: '1px solid #B1BCC8',
                  }}
                />
                {premiumSelected && (
                  <div
                    style={{
                      width: '10px',
                      height: '10px',
                      borderRadius: '100%',
                      position: 'absolute',
                      background: '#056DFF',
                      top: '5px',
                      left: '5px',
                    }}
                  />
                )}
              </div>

              <div style={{ textAlign: 'left' }}>
                <p style={{ fontWeight: 'bold', marginBottom: '4px' }}>Just me</p>
                <p style={{ marginBottom: '0' }}>
                  You need a private vault that keeps your logins safe and ready when you are.
                </p>
              </div>
            </button>
            <button
              style={{
                borderRadius: '8px',
                border: '1px solid #D5DBE2',
                padding: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                background: !premiumSelected ? '#EBF2FA' : '#fff',
              }}
              onClick={() => {
                setPremiumSelected(false);
                gtagEnvelope('event', 'modal_radio_click', {
                  cta_name: 'My Team',
                  cta_position: 'Unified Trial Modal - Business',
                });
                postSegmentService({
                  event: `${getEventName(true)}_trial_form_radio_clicked`,
                  properties: {
                    journey_step: 'Step 2 select trial',
                    sku: PRODUCT_SKUS.SKU_LPBUSINESS,
                  },
                });
              }}
            >
              <div style={{ position: 'relative' }}>
                <div
                  style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '100%',
                    border: '1px solid #B1BCC8',
                  }}
                />
                {!premiumSelected && (
                  <div
                    style={{
                      width: '10px',
                      height: '10px',
                      borderRadius: '100%',
                      position: 'absolute',
                      background: '#056DFF',
                      top: '5px',
                      left: '5px',
                    }}
                  />
                )}
              </div>
              <div style={{ textAlign: 'left' }}>
                <p style={{ fontWeight: 'bold', marginBottom: '4px' }}>My team</p>
                <p style={{ marginBottom: '0' }}>
                  You need a secure way for each teammate to manage their own vault—and share when it matters.
                </p>
              </div>
            </button>
          </div>
        )}
        {trialAllowed && (
          <Formik<SendAsyncPostObject>
            validateOnMount={true}
            validateOnChange={true}
            validateOnBlur={true}
            innerRef={formikRef}
            initialValues={initialValues}
            onSubmit={async (values) => {
              const submissionValues: SendAsyncPostObject = zoomInfoData
                ? {
                    ...values,
                    fullName: zoomInfoData.hasFirstName && zoomInfoData.hasLastName ? null : values.fullName,
                    title: zoomInfoData.hasJobTitle ? null : values.title,
                    company: zoomInfoData.hasCompanyName ? null : values.company,
                    employees: zoomInfoData.hasCompanyEmployeeCount ? null : values.employees,
                  }
                : values;
              await handleSub(submissionValues);
            }}
          >
            {({ errors, touched, isSubmitting, isValidating, isValid, submitCount }) => {
              return (
                <Form>
                  <FormikEffect
                    submitCount={submitCount}
                    isValidating={isValidating}
                    isValid={isValid}
                    errors={errors}
                    isSubmitting={isSubmitting}
                    setClosedBanner={setClosedBanner}
                    formikRef={formikRef}
                    successUrl={data.successUrl}
                    trialLength={trialLength}
                    emailStep={step === 1}
                  />
                  {!trialAllowed ? (
                    <></>
                  ) : (
                    <BusinessInputTest
                      formikTouched={touched}
                      formikErrors={errors}
                      inputs={data.inputs}
                      showHint={false}
                      trackErrorBlur={trackErrorBlur}
                      validateBusinessEmail={validateBusinessEmail}
                      existingAccount={existingAccount}
                      userEmail={existingUserEmail}
                      existingEmailError={data.existingEmailError}
                      step={step}
                      mistypedEmailError={mistypedEmail}
                      zoomInfoData={zoomInfoData}
                    />
                  )}
                  {step === 1 ? (
                    <div className={styles.buttonHolder} style={{ alignItems: 'center' }}>
                      <div style={{ display: 'flex', gap: '12px' }}>
                        <div
                          style={{
                            height: '4px',
                            background: '#D71340',
                            width: '24px',
                            borderRadius: '10px',
                          }}
                        />
                        <div
                          style={{
                            height: '4px',
                            background: '#D0D1D1',
                            width: '4px',
                            borderRadius: '100px',
                          }}
                        />
                        <div
                          style={{
                            height: '4px',
                            background: '#D0D1D1',
                            width: '4px',
                            borderRadius: '100px',
                          }}
                        />
                      </div>
                      <CommonButton
                        type="submit"
                        id="EMAIL_SUBMIT"
                        disabled={formSubmitting}
                        modifier={ctaClassNameModifier ?? undefined}
                        onClick={() => {
                          goToNextStep();
                        }}
                      >
                        {formSubmitting ? (
                          <Spinner
                            colorBottom={
                              typeof ctaClassNameModifier === 'string' && ctaClassNameModifier.length > 0
                                ? 'rgba(0,0,0,0.5)'
                                : '#751a17'
                            }
                            colorTop="#fff"
                          />
                        ) : (
                          'Next'
                        )}
                      </CommonButton>
                    </div>
                  ) : step === 2 ? (
                    <div className={styles.buttonHolder} style={{ alignItems: 'center' }}>
                      <div style={{ display: 'flex', gap: '12px' }}>
                        <div
                          style={{
                            height: '4px',
                            background: '#D71340',
                            width: '24px',
                            borderRadius: '10px',
                          }}
                        />
                        <div
                          style={{
                            height: '4px',
                            background: '#D0D1D1',
                            width: '4px',
                            borderRadius: '100px',
                          }}
                        />
                        {!premiumSelected && (
                          <div
                            style={{
                              height: '4px',
                              background: '#D0D1D1',
                              width: '4px',
                              borderRadius: '100px',
                            }}
                          />
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '24px' }}>
                        <CommonButton
                          type="button"
                          link={true}
                          onClick={() => {
                            setStep(1);
                            postSegmentService({
                              event: `${getEventName(true)}_trial_form_shown`,
                              properties: {
                                journey_step: `Step 1 email`,
                                sku: 'UNIFIED',
                              },
                            });
                          }}
                        >
                          {'Back'}
                        </CommonButton>
                        {premiumSelected ? (
                          <CommonButton
                            id="FORM_SUBMIT"
                            type="submit"
                            disabled={formSubmitting}
                            modifier={ctaClassNameModifier ?? undefined}
                          >
                            {formSubmitting ? (
                              <Spinner
                                colorBottom={
                                  typeof ctaClassNameModifier === 'string' && ctaClassNameModifier.length > 0
                                    ? 'rgba(0,0,0,0.5)'
                                    : '#751a17'
                                }
                                colorTop="#fff"
                              />
                            ) : (
                              'Next'
                            )}
                          </CommonButton>
                        ) : (
                          <CommonButton
                            type="button"
                            disabled={formSubmitting}
                            onClick={(e) => {
                              goToStep3();
                            }}
                          >
                            {formSubmitting ? (
                              <Spinner
                                colorBottom={
                                  typeof ctaClassNameModifier === 'string' && ctaClassNameModifier.length > 0
                                    ? 'rgba(0,0,0,0.5)'
                                    : '#751a17'
                                }
                                colorTop="#fff"
                              />
                            ) : (
                              'Next'
                            )}
                          </CommonButton>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className={styles.buttonHolder} style={{ alignItems: 'center' }}>
                      <div style={{ display: 'flex', gap: '12px' }}>
                        <div
                          style={{
                            height: '4px',
                            background: '#D0D1D1',
                            width: '4px',
                            borderRadius: '100px',
                          }}
                        />
                        <div
                          style={{
                            height: '4px',
                            background: '#D71340',
                            width: '24px',
                            borderRadius: '10px',
                          }}
                        />
                        <div
                          style={{
                            height: '4px',
                            background: '#D0D1D1',
                            width: '4px',
                            borderRadius: '100px',
                          }}
                        />
                      </div>
                      <div style={{ display: 'flex', gap: '24px' }}>
                        <CommonButton
                          type="button"
                          link={true}
                          onClick={() => {
                            resetStep3State();
                            setStep(2);
                            postSegmentService({
                              event: `${getEventName(true)}_trial_form_shown`,
                              properties: {
                                journey_step: `Step 2 select trial`,
                                sku: premiumSelected ? PRODUCT_SKUS.SKU_LPPREMIUM : PRODUCT_SKUS.SKU_LPBUSINESS,
                              },
                            });
                          }}
                        >
                          {'Back'}
                        </CommonButton>
                        <CommonButton
                          id="FORM_SUBMIT"
                          type="submit"
                          disabled={formSubmitting}
                          modifier={ctaClassNameModifier ?? undefined}
                        >
                          {formSubmitting ? (
                            <Spinner
                              colorBottom={
                                typeof ctaClassNameModifier === 'string' && ctaClassNameModifier.length > 0
                                  ? 'rgba(0,0,0,0.5)'
                                  : '#751a17'
                              }
                              colorTop="#fff"
                            />
                          ) : (
                            'Next'
                          )}
                        </CommonButton>
                      </div>
                    </div>
                  )}
                  {step === 1 && (
                    <div onClick={unsubscribeClick} className={styles.terms}>
                      <p style={{ textAlign: 'left' }}>
                        <ConvertStringToReact
                          htmlString={isCanadaUser && data!.canada_Terms ? data!.canada_Terms : data!.terms}
                        />
                      </p>
                      {displayUnsubscribeCheckbox && (
                        <Field
                          name="optOutOfEmails"
                          as={Checkbox}
                          checked={!contactPermission}
                          value={contactPermission}
                          label={data!.optOutOfEmails![0].label}
                          id="optOutOfEmails"
                          onClick={() => {
                            setOptOutOfEmails();
                          }}
                        />
                      )}
                    </div>
                  )}
                </Form>
              );
            }}
          </Formik>
        )}
      </div>
      {step === 2 && (
        <div
          style={{
            flex: 1,
            padding: isMobile ? '24px' : '80px 60px',
            background: premiumSelected ? '#F1F8F1' : '#E5ECF4',
            color: '#fff',
            alignItems: 'center',
            justifyContent: 'center',
            display: 'flex',
          }}
          data-theme="2"
        >
          {premiumSelected ? (
            <div
              style={{
                background: '#0E534F',
                padding: '40px',
                borderRadius: '8px',
                display: 'flex',
                gap: '10px',
                flexDirection: 'column',
              }}
            >
              <GreenPerson />
              <div
                style={{
                  fontSize: '22px',
                  lineHeight: '28px',
                  fontWeight: '500',
                }}
              >
                LastPass Premium—free for 30 days
              </div>
              <div>
                <div
                  style={{
                    display: 'flex',
                    gap: '16px',
                    flexDirection: 'row',
                    marginTop: '8px',
                  }}
                >
                  <div>
                    <Sheild />
                  </div>
                  <p>LastPass Premium protects your digital life, on your terms.</p>
                </div>
                <div
                  style={{
                    display: 'flex',
                    gap: '16px',
                    flexDirection: 'row',
                  }}
                >
                  <div>
                    <Password />
                  </div>
                  <p>Securely store unlimited passwords and access them anytime, on any device.</p>
                </div>
                <div
                  style={{
                    display: 'flex',
                    gap: '16px',
                    flexDirection: 'row',
                  }}
                >
                  <div>
                    <Nav />
                  </div>
                  <div>
                    <p>
                      After 30 days, <b>the choice is yours</b>:
                    </p>
                    <ul>
                      <li>Go Premium for access on all devices</li>
                      <li>Or use Free on either all computers or all mobile devices</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div
              style={{
                background: '#3B4A6D',
                padding: '40px',
                borderRadius: '8px',
                display: 'flex',
                gap: '10px',
                flexDirection: 'column',
              }}
            >
              <Business />
              <div
                style={{
                  fontSize: '22px',
                  lineHeight: '28px',
                  fontWeight: '500',
                }}
              >
                LastPass Business—free for 14 days
              </div>
              <div>
                <div
                  style={{
                    display: 'flex',
                    gap: '16px',
                    flexDirection: 'row',
                    marginTop: '8px',
                  }}
                >
                  <div>
                    <Vault />
                  </div>
                  <p>
                    LastPass Business protects your organization’s data with flexible controls and powerful tools that
                    scale with you.
                  </p>
                </div>
                <div
                  style={{
                    display: 'flex',
                    gap: '16px',
                    flexDirection: 'row',
                  }}
                >
                  <div>
                    <Reporting />
                  </div>
                  <p>Each person gets their own secure vault—to store, share, and protect what matters most.</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
